import { compareDates } from "./dates";
import { comparePositions } from "./position";
import { isOverdue, isUserVisible } from "./predicates";
import { isActiveOn, progressOn, type RecurrenceProgress } from "./recurrence";
import { isTerminal } from "./statuses";
import type { CompletionLike, NodeLike, PlacementLike, PlainDate } from "./types";

/**
 * Чистые выборки над абстрактной коллекцией. Их считает клиент (локально, из стора)
 * и сервер (`core/views`, для REST/MCP-агента) — одними и теми же функциями,
 * иначе стороны разъедутся (offline-sync §3).
 */

/**
 * Строка аутлайна: родной ребёнок или вплетение. `position` берётся у вхождения,
 * а не у ноды — одна нода в разных контейнерах стоит на разных позициях.
 */
export type OutlineEntry<N extends NodeLike = NodeLike> = {
	node: N;
	position: string;
	/** Вплетение — строка `node_placements`; жесты над вхождением и над нодой различаются (ui-spec 3.3). */
	placement: PlacementLike | null;
};

/**
 * Содержимое контейнера: родные дети ∪ вплетения, единый keyspace, `ORDER BY position, id`.
 * Вторичный ключ — id **ноды**: именно он однозначен в объединении двух таблиц.
 */
export function containerEntries<N extends NodeLike>(
	nodes: readonly N[],
	placements: readonly PlacementLike[],
	containerId: string
): OutlineEntry<N>[] {
	const byId = new Map(nodes.map((n) => [n.id, n]));
	const entries: OutlineEntry<N>[] = [];

	for (const node of nodes) {
		if (node.parentId === containerId) {
			entries.push({ node, position: node.position, placement: null });
		}
	}
	for (const placement of placements) {
		if (placement.containerId !== containerId) {
			continue;
		}
		const node = byId.get(placement.nodeId);
		if (node) {
			entries.push({ node, position: placement.position, placement });
		}
	}

	return entries.sort((a, b) =>
		comparePositions({ position: a.position, id: a.node.id }, { position: b.position, id: b.node.id })
	);
}

/** Панель «срок на дату»: ВСЕ ноды с этой датой, включая терминальные. Кроме технических ролей. */
export function scheduledOn<N extends NodeLike>(nodes: readonly N[], date: PlainDate): N[] {
	return nodes
		.filter((n) => n.archivedAt === null && isUserVisible(n) && n.scheduledDate === date)
		.sort((a, b) => comparePositions(a, b));
}

export type OverdueEntry<N extends NodeLike = NodeLike> = {
	node: N;
	/** С какой даты просрочено: срок ноды или — у задачи без срока — её день. */
	date: PlainDate;
	/** `scheduled` — просрочен срок; `day` — задача без срока осталась в прошедшем дне. */
	reason: "scheduled" | "day";
};

/**
 * Просрочено: две разные причины в одном списке. Ноды не двигаются — это линза.
 *
 * 1. **Срок в прошлом** (`isOverdue`) — любая пользовательская роль, как и было.
 * 2. **Задача без срока в прошедшем дне.** Написал задачу в «Сегодня», даты не ставил —
 *    назавтра она здесь. Только роль `task`: заметка в дне обязательством не является,
 *    а у проекта и фокуса своя жизнь дольше одного дня.
 *
 * «В дне» — где угодно в поддереве day-ноды (задача под заголовком внутри дня — тоже),
 * а также вплетённая туда. Берётся САМЫЙ ПОЗДНИЙ из дней, где задача показывается: вплёл
 * вчерашнюю в сегодня — значит, перенёс, и просрочкой она не считается.
 *
 * Срок, если он есть, главнее дня: задача во вчерашнем дне со сроком на завтра не просрочена.
 * Сортировка обратно-хронологическая — свежая просрочка сверху.
 */
export function overdue<N extends NodeLike>(
	nodes: readonly N[],
	placements: readonly PlacementLike[],
	today: PlainDate
): OverdueEntry<N>[] {
	const byId = new Map(nodes.map((n) => [n.id, n]));
	const dayMemo = new Map<string, PlainDate | null>();

	/** День, в поддереве которого лежит нода (включая её саму); null — не в дне. */
	const dayOf = (id: string | null): PlainDate | null => {
		const path: string[] = [];
		let found: PlainDate | null = null;
		let cursor = id;
		while (cursor !== null) {
			const memo = dayMemo.get(cursor);
			if (memo !== undefined) {
				found = memo;
				break;
			}
			const node = byId.get(cursor);
			if (!node) {
				break;
			}
			path.push(cursor);
			if (node.role === "day") {
				found = node.dayDate;
				break;
			}
			cursor = node.parentId;
		}
		for (const visited of path) {
			dayMemo.set(visited, found);
		}
		return found;
	};

	const placedIn = new Map<string, string[]>();
	for (const p of placements) {
		const list = placedIn.get(p.nodeId);
		if (list) {
			list.push(p.containerId);
		} else {
			placedIn.set(p.nodeId, [p.containerId]);
		}
	}

	const out: OverdueEntry<N>[] = [];
	for (const node of nodes) {
		if (!isUserVisible(node) || node.archivedAt !== null) {
			continue;
		}
		if (node.scheduledDate !== null) {
			if (isOverdue(node, today)) {
				out.push({ node, date: node.scheduledDate, reason: "scheduled" });
			}
			continue;
		}
		if (node.role !== "task" || isTerminal(node.status)) {
			continue;
		}
		let latest: PlainDate | null = null;
		for (const containerId of [node.parentId, ...(placedIn.get(node.id) ?? [])]) {
			const day = dayOf(containerId);
			if (day !== null && (latest === null || compareDates(day, latest) > 0)) {
				latest = day;
			}
		}
		if (latest !== null && compareDates(latest, today) < 0) {
			out.push({ node, date: latest, reason: "day" });
		}
	}

	return out.sort((a, b) => compareDates(b.date, a.date) || comparePositions(a.node, b.node));
}

export type RecurringEntry<N extends NodeLike = NodeLike> = {
	node: N;
	progress: RecurrenceProgress;
};

/**
 * Панель повторяющихся на дату: активные (период матчит дату) с прогрессом «x/N».
 * Достигнутость цели не фильтрует — её видно в самом прогрессе.
 */
export function recurringOn<N extends NodeLike>(
	nodes: readonly N[],
	completions: readonly CompletionLike[],
	date: PlainDate
): RecurringEntry<N>[] {
	const byNode = new Map<string, CompletionLike[]>();
	for (const c of completions) {
		const list = byNode.get(c.nodeId);
		if (list) {
			list.push(c);
		} else {
			byNode.set(c.nodeId, [c]);
		}
	}

	const out: RecurringEntry<N>[] = [];
	for (const node of nodes) {
		if (node.role !== "recurring" || node.archivedAt !== null || node.recurrence === null) {
			continue;
		}
		if (!isActiveOn(node.recurrence, date)) {
			continue;
		}
		out.push({ node, progress: progressOn(node.recurrence, date, byNode.get(node.id) ?? []) });
	}
	return out.sort((a, b) => comparePositions(a.node, b.node));
}

/** Все повторяющиеся — «бесплатный» глобальный раздел. */
export function allRecurring<N extends NodeLike>(nodes: readonly N[]): N[] {
	return nodes.filter((n) => n.role === "recurring" && n.archivedAt === null).sort(comparePositions);
}

export function projects<N extends NodeLike>(nodes: readonly N[]): N[] {
	return nodes.filter((n) => n.role === "project" && n.archivedAt === null).sort(comparePositions);
}

/**
 * Все заходы в фокус: сначала открытые (в порядке дерева), под ними закрытые —
 * свежие сверху. Две сортировки, а не одна, потому что это два разных списка в одном:
 * у открытых порядок задаёт рука, у закрытых — время, и другого у них нет.
 *
 * `completed_at` заполняется ТОЛЬКО при входе в `done` (data-model §2.1), поэтому
 * отменённый фокус даты не имеет и уходит в конец — это правильно: решение по нему
 * принято, но «когда отработан» у него не существует.
 */
export function focuses<N extends NodeLike>(nodes: readonly N[]): N[] {
	const all = nodes.filter((n) => n.role === "focus" && n.archivedAt === null);
	const open = all.filter((n) => !isTerminal(n.status)).sort(comparePositions);
	const closed = all
		.filter((n) => isTerminal(n.status))
		.sort((a, b) => {
			// Позиция — вторичный ключ, а не украшение: две ноды, закрытые одним жестом,
			// имеют РАВНОЕ время, и без неё порядок разъехался бы с серверным (`core/getFocuses`).
			if (a.completedAt === null || b.completedAt === null) {
				return a.completedAt === b.completedAt ? comparePositions(a, b) : a.completedAt === null ? 1 : -1;
			}
			const byTime = b.completedAt.getTime() - a.completedAt.getTime();
			return byTime === 0 ? comparePositions(a, b) : byTime;
		});
	return [...open, ...closed];
}

/** Панель справа: только то, чем занят сейчас. Закрытые фокусы живут на `/focus`. */
export function activeFocuses<N extends NodeLike>(nodes: readonly N[]): N[] {
	return nodes
		.filter((n) => n.role === "focus" && n.archivedAt === null && !isTerminal(n.status))
		.sort(comparePositions);
}

/** Бэклинки «Упоминается в». Показываются только живые ноды — архива в реплике нет. */
export function backlinks<N extends NodeLike>(
	nodes: readonly N[],
	links: readonly { fromNodeId: string; toNodeId: string; relation: string }[],
	nodeId: string
): N[] {
	const from = new Set(
		links.filter((l) => l.toNodeId === nodeId && l.relation === "mention").map((l) => l.fromNodeId)
	);
	return nodes.filter((n) => from.has(n.id) && n.archivedAt === null).sort(comparePositions);
}

/** «Что делать дальше» для агента: живые нетерминальные задачи, просроченные первыми. */
export function whatsNext<N extends NodeLike>(nodes: readonly N[], today: PlainDate, limit = 10): N[] {
	return nodes
		.filter(
			(n) =>
				n.archivedAt === null &&
				(n.role === "task" || n.role === "project") &&
				!isTerminal(n.status) &&
				n.status !== "someday"
		)
		.sort((a, b) => {
			const ao = isOverdue(a, today) ? 0 : 1;
			const bo = isOverdue(b, today) ? 0 : 1;
			if (ao !== bo) {
				return ao - bo;
			}
			if (a.scheduledDate !== b.scheduledDate) {
				if (a.scheduledDate === null) {
					return 1;
				}
				if (b.scheduledDate === null) {
					return -1;
				}
				return compareDates(a.scheduledDate, b.scheduledDate);
			}
			return comparePositions(a, b);
		})
		.slice(0, limit);
}
